// v4 (V4-D6 parity with set_model_rules / edit_scene tags): the web's read of a video's model rules and each scene's
// model (GET /api/projects/:id/model-rules, V4-D1). Rules are saved through the set_model_rules tool (tapTool), the
// same words the Director takes ("Kling for action, Gen-4.5 for dialogue"). Under VITE_MOCK the answer is made here.
import { call } from "./api";

export type SceneModel = { n: number; sceneId: string; tags: string[]; model: string; short: string; source: string; rule: string | null };
export type ModelRulesView = { video: string[]; series: string[]; inSeries: boolean; scenes: SceneModel[] };

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown) => (typeof v === "string" ? v : "");
const words = (v: unknown) => arr(v).map((r) => str(obj(r).words)).filter(Boolean);

/** The API's answer, checked. */
export function normModelRules(raw: unknown): ModelRulesView {
  const o = obj(raw);
  const rules = obj(o.rules);
  return {
    video: words(rules.video),
    series: words(rules.series),
    inSeries: typeof rules.collectionId === "string" && !!rules.collectionId,
    scenes: arr(o.scenes)
      .map(obj)
      .filter((s) => typeof s.sceneId === "string")
      .map((s, i) => ({
        n: typeof s.n === "number" ? s.n : i + 1,
        sceneId: s.sceneId as string,
        tags: arr(s.tags).filter((t): t is string => typeof t === "string"),
        model: str(s.model),
        short: str(s.short) || str(s.model),
        source: str(s.source),
        rule: typeof s.rule === "string" && s.rule ? s.rule : null,
      })),
  };
}

/** "Kling 2.5 · action, close-up": a scene's model and its tags, for one line. */
export const sceneModelLine = (s: Pick<SceneModel, "short" | "tags">) => [s.short, s.tags.join(", ")].filter(Boolean).join(" · ");

/** Tags as the user types them ("Action, close up;b-roll") -> the words edit_scene takes ("action, close up, b-roll"). */
export const tagWords = (text: string) =>
  text
    .split(/[,;\n]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
    .filter((t, i, all) => all.indexOf(t) === i)
    .join(", ");

const isMock = () => import.meta.env?.VITE_MOCK === "1";

export async function loadModelRules(projectId: string, sceneIds: string[] = []): Promise<ModelRulesView> {
  if (isMock()) {
    const tagSets = [["product", "close-up"], ["action"], ["dialogue"], ["landscape", "b-roll"], ["text"]];
    return {
      // X3: the default is Kling 2.5 Turbo Pro (as in production), so the mock's rule picks something else for dialogue
      video: ["Runway Gen-4.5 for dialogue"],
      series: [],
      inSeries: false,
      scenes: sceneIds.map((id, i) => {
        const tags = tagSets[i % tagSets.length]!;
        const rule = tags.includes("dialogue");
        return { n: i + 1, sceneId: id, tags, model: rule ? "runway:gen4.5" : "fal:fal-ai/kling-video/v2.5-turbo/pro/image-to-video", short: rule ? "Gen-4.5" : "Kling 2.5", source: rule ? "rule" : "default", rule: rule ? "Runway Gen-4.5 for dialogue" : null };
      }),
    };
  }
  return normModelRules(await call("GET", `projects/${encodeURIComponent(projectId)}/model-rules`));
}
